// src/components/CheckoutStudentDetail.jsx
//
// Full detail view for a single student's checkout record. Lists every
// relevant section in canonical order with its status, signoff metadata, and
// action buttons gated by the user's permissions.
//
// This component:
//   - Subscribes to the record document in real time (onSnapshot)
//   - Renders each section with its status pill, signed-by info, and notes
//   - Shows action buttons (Sign off / Undo / Mark N/A) gated by permissions
//   - Coordinates the four modals (sign, undo, dorm sign, dorm undo, na)
//   - Provides a "Change form type" button for superadmins
//   - Provides a "Download PDF" button for anyone with read access
//
// Props:
//   recordId: Firestore doc ID of the checkout record
//   user: current auth user (incl. isSuperAdmin, checkoutSections, checkoutOverrides)
//   onBack: callback to return to the dashboard

import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

import {
  CHECKOUT_SECTIONS,
  SECTION_STATUS,
  RECORD_STATUS,
  getRelevantSectionIds,
  getSectionConfig,
  getFormConfig,
  computeCompletionPercent,
  formatSignoffTimestamp,
  getStatusLabel,
  getStatusColor,
  canSignOffSection,
  KA_COLORS,
} from '../utils/checkoutHelpers';
import { generateCheckoutPdf } from '../utils/checkoutPdf';

import CheckoutSignoffModal from './CheckoutSignoffModal';
import CheckoutDormSignoffModal from './CheckoutDormSignoffModal';
import CheckoutFormChangeModal from './CheckoutFormChangeModal';
import SignoffHistoryView from './SignoffHistoryView';
import EditRecordSectionsModal from './EditRecordSectionsModal';


// -----------------------------------------------------------------------------

const styles = {
  page: {
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    color: '#222',
    maxWidth: 1100,
    margin: '0 auto',
    padding: '16px 20px',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  backBtn: {
    background: 'none',
    border: `1px solid ${KA_COLORS.borderGray}`,
    padding: '6px 12px',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    color: '#333',
  },
  spacer: { flex: 1 },
  iconBtn: {
    padding: '8px 14px',
    border: `1px solid ${KA_COLORS.borderGray}`,
    background: '#fff',
    color: KA_COLORS.navy,
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
  },
  primaryBtn: {
    padding: '8px 14px',
    border: 'none',
    background: KA_COLORS.red,
    color: '#fff',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
  },
  headerCard: {
    background: '#fff',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderTop: `4px solid ${KA_COLORS.red}`,
    borderRadius: 6,
    padding: 20,
    marginBottom: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  studentName: {
    fontSize: 22,
    fontWeight: 700,
    color: KA_COLORS.navy,
    margin: 0,
  },
  studentMeta: { fontSize: 14, color: '#555', margin: 0 },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
    marginTop: 4,
    fontSize: 13,
  },
  formPill: {
    background: KA_COLORS.navy,
    color: '#fff',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
  },
  progressOuter: {
    flex: 1,
    height: 10,
    background: KA_COLORS.borderGray,
    borderRadius: 5,
    overflow: 'hidden',
    minWidth: 200,
  },
  progressInner: (pct, complete) => ({
    width: `${pct}%`,
    height: '100%',
    background: complete ? '#2E7D32' : KA_COLORS.gold,
    transition: 'width 0.3s',
  }),
  progressLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: KA_COLORS.navy,
    minWidth: 80,
    textAlign: 'right',
  },
  groupHeader: {
    fontSize: 12,
    fontWeight: 700,
    color: KA_COLORS.gold,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    margin: '24px 0 8px',
  },
  sectionsList: { display: 'flex', flexDirection: 'column', gap: 10 },
  sectionCard: {
    background: '#fff',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 6,
    padding: 14,
  },
  sectionRow: {
    display: 'flex',
    gap: 14,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  sectionMain: { flex: 1, minWidth: 240 },
  sectionLabel: {
    fontSize: 15,
    fontWeight: 600,
    color: KA_COLORS.navy,
    margin: 0,
  },
  sectionDesc: {
    fontSize: 12,
    color: '#666',
    margin: '4px 0 0',
    lineHeight: 1.4,
  },
  teacherLine: {
    fontSize: 12.5,
    color: KA_COLORS.navy,
    margin: '6px 0 0',
    lineHeight: 1.4,
  },
  teacherCountLine: {
    fontSize: 12.5,
    color: KA_COLORS.navy,
    margin: '8px 0 4px',
    lineHeight: 1.4,
  },
  teacherList: {
    margin: '4px 0 6px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  teacherRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '8px 10px',
    background: '#FAFAFA',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
  },
  teacherRowMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  teacherRowName: {
    fontSize: 13,
    fontWeight: 600,
    color: KA_COLORS.navy,
  },
  teacherRowSisId: {
    fontWeight: 400,
    color: '#888',
    fontSize: 12,
  },
  teacherRowMineTag: {
    display: 'inline-block',
    marginLeft: 6,
    padding: '1px 6px',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#fff',
    background: KA_COLORS.gold,
    borderRadius: 3,
  },
  teacherRowSigned: {
    fontSize: 11.5,
    color: '#1B5E20',
  },
  teacherRowPending: {
    fontSize: 11.5,
    color: '#996515',
    fontWeight: 600,
  },
  teacherRowActions: {
    display: 'flex',
    gap: 6,
  },
  teacherLabel: {
    fontWeight: 600,
    color: '#555',
  },
  contextLine: {
    fontSize: 12.5,
    color: '#222',
    margin: '4px 0 0',
    lineHeight: 1.4,
    background: '#FFF8E1',
    border: '1px solid #FFE082',
    padding: '4px 8px',
    borderRadius: 3,
  },
  contextLabel: {
    fontWeight: 600,
    color: '#7A5C00',
  },
  signoffInfo: {
    fontSize: 12,
    color: '#444',
    marginTop: 8,
    lineHeight: 1.5,
  },
  notesBox: {
    marginTop: 6,
    padding: '6px 8px',
    background: KA_COLORS.lightGray,
    borderRadius: 3,
    border: `1px solid ${KA_COLORS.borderGray}`,
    fontSize: 12,
    color: '#444',
    fontStyle: 'italic',
  },
  rightCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 8,
    minWidth: 130,
  },
  statusPill: (status) => {
    const c = getStatusColor(status);
    return {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: 12,
      background: c.bg,
      color: c.fg,
      border: `1px solid ${c.border}`,
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    };
  },
  actionsRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  actionBtn: (variant) => {
    const palette = {
      primary: { bg: KA_COLORS.red, fg: '#fff', border: KA_COLORS.red },
      secondary: { bg: '#fff', fg: KA_COLORS.navy, border: KA_COLORS.borderGray },
      muted: { bg: '#fff', fg: '#777', border: KA_COLORS.borderGray },
    };
    const p = palette[variant] || palette.secondary;
    return {
      padding: '5px 10px',
      border: `1px solid ${p.border}`,
      background: p.bg,
      color: p.fg,
      borderRadius: 4,
      cursor: 'pointer',
      fontSize: 12,
      fontWeight: 600,
    };
  },
  actionBtnSmall: (variant) => {
    const palette = {
      primary: { bg: KA_COLORS.red, fg: '#fff', border: KA_COLORS.red },
      secondary: { bg: '#fff', fg: KA_COLORS.navy, border: KA_COLORS.borderGray },
      muted: { bg: '#fff', fg: '#777', border: KA_COLORS.borderGray },
    };
    const p = palette[variant] || palette.secondary;
    return {
      padding: '3px 8px',
      border: `1px solid ${p.border}`,
      background: p.bg,
      color: p.fg,
      borderRadius: 3,
      cursor: 'pointer',
      fontSize: 11,
      fontWeight: 600,
    };
  },
  unauthorizedNote: {
    fontSize: 11,
    color: '#999',
    fontStyle: 'italic',
    marginTop: 4,
    textAlign: 'right',
  },
  loadingBox: {
    padding: 40,
    textAlign: 'center',
    color: '#666',
    fontStyle: 'italic',
  },
  errorBox: {
    padding: 16,
    background: '#FFEBEE',
    border: '1px solid #FFCDD2',
    borderRadius: 4,
    color: '#C62828',
    fontSize: 13,
    margin: '20px 0',
  },
  inventoryPreview: {
    marginTop: 8,
    padding: 8,
    background: KA_COLORS.lightGray,
    borderRadius: 4,
    border: `1px solid ${KA_COLORS.borderGray}`,
    fontSize: 11,
    color: '#444',
  },
  inventoryHeading: {
    fontWeight: 600,
    color: KA_COLORS.navy,
    marginBottom: 4,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  inventoryItems: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '2px 8px',
  },
};

// -----------------------------------------------------------------------------
// Group sections by their `group` field (for the "Course Teacher" cluster).
// Returns an array of { groupName | null, sections: [...] } in display order.
// -----------------------------------------------------------------------------

const groupSections = (sectionConfigs) => {
  const out = [];
  let currentGroup = undefined; // sentinel — first section will always force a new bucket
  let currentBucket = null;
  sectionConfigs.forEach((s) => {
    const g = s.group || null;
    if (g !== currentGroup) {
      currentGroup = g;
      currentBucket = { groupName: g, sections: [] };
      out.push(currentBucket);
    }
    currentBucket.sections.push(s);
  });
  return out;
};

// -----------------------------------------------------------------------------

const SectionCard = ({
  section,
  data,
  user,
  onSign,
  onUndo,
  onMarkNa,
}) => {
  const status = data?.status || SECTION_STATUS.PENDING;
  const canSign = canSignOffSection(user, section.id);
  const isSuperAdmin = !!user?.isSuperAdmin;
  const myStaffSisId = user?.staffSisId ? String(user.staffSisId) : null;

  const hasTeachers =
    Array.isArray(data?.teachers) && data.teachers.length > 0;

  // Section-level actions (right column).
  // For multi-teacher sections we show different controls than single-signer
  // sections: the per-teacher Sign buttons live INSIDE the card body, not in
  // the right-column actions. Right-column actions for multi-teacher are
  // mostly admin overrides.
  let actions = null;
  if (hasTeachers) {
    // Multi-teacher: only show the right-column Mark N/A (admin-only) when
    // the section is still pending. Undo for fully-signed sections is also
    // admin-only and undoes ALL teachers (per-teacher undo lives inline).
    if (status === SECTION_STATUS.PENDING && isSuperAdmin) {
      actions = (
        <div style={styles.actionsRow}>
          <button
            type="button"
            style={styles.actionBtn('muted')}
            onClick={() => onMarkNa(section.id)}
          >
            Mark N/A
          </button>
        </div>
      );
    } else if (status === SECTION_STATUS.SIGNED && isSuperAdmin) {
      actions = (
        <div style={styles.actionsRow}>
          <button
            type="button"
            style={styles.actionBtn('secondary')}
            onClick={() => onUndo(section.id)}
            title="Undo all teacher signoffs"
          >
            Undo all
          </button>
        </div>
      );
    } else if (status === SECTION_STATUS.NA && isSuperAdmin) {
      actions = (
        <div style={styles.actionsRow}>
          <button
            type="button"
            style={styles.actionBtn('secondary')}
            onClick={() => onSign(section.id)}
          >
            Reactivate
          </button>
        </div>
      );
    }
  } else if (status === SECTION_STATUS.PENDING) {
    actions = canSign ? (
      <div style={styles.actionsRow}>
        <button
          type="button"
          style={styles.actionBtn('primary')}
          onClick={() => onSign(section.id)}
        >
          Sign off
        </button>
        {isSuperAdmin && (
          <button
            type="button"
            style={styles.actionBtn('muted')}
            onClick={() => onMarkNa(section.id)}
          >
            Mark N/A
          </button>
        )}
      </div>
    ) : (
      <span style={styles.unauthorizedNote}>
        Not authorized for this section
      </span>
    );
  } else if (status === SECTION_STATUS.SIGNED) {
    actions = canSign ? (
      <div style={styles.actionsRow}>
        <button
          type="button"
          style={styles.actionBtn('secondary')}
          onClick={() => onUndo(section.id)}
        >
          Undo
        </button>
      </div>
    ) : null;
  } else if (status === SECTION_STATUS.NA) {
    actions = isSuperAdmin ? (
      <div style={styles.actionsRow}>
        <button
          type="button"
          style={styles.actionBtn('secondary')}
          onClick={() => onSign(section.id)}
        >
          Reactivate &amp; sign
        </button>
      </div>
    ) : null;
  }

  // For multi-teacher: how many of N teachers are signed?
  let teachersHeader = null;
  if (hasTeachers) {
    const signedCount = data.teachers.filter((t) => !!t.signedAt).length;
    const total = data.teachers.length;
    teachersHeader = (
      <p style={styles.teacherCountLine}>
        <span style={styles.teacherLabel}>Teachers:</span>{' '}
        <strong>{signedCount}</strong> of <strong>{total}</strong> cleared
      </p>
    );
  }

  return (
    <div style={styles.sectionCard}>
      <div style={styles.sectionRow}>
        <div style={styles.sectionMain}>
          <p style={styles.sectionLabel}>{section.label}</p>
          {section.description && (
            <p style={styles.sectionDesc}>{section.description}</p>
          )}

          {/* Single-teacher (legacy) display — only shown for sections that
              don't have a teachers[] array. Newer records always use the
              array, so this branch effectively only fires for old data. */}
          {!hasTeachers && data.teacherName && (
            <p style={styles.teacherLine}>
              <span style={styles.teacherLabel}>Teacher:</span>{' '}
              {data.teacherName}
            </p>
          )}

          {/* Free-text context — which book, which device, which sport.
              Uploaded via CSV; varies per section per student. */}
          {data.context && (
            <p style={styles.contextLine}>
              <span style={styles.contextLabel}>Notes:</span> {data.context}
            </p>
          )}

          {/* Multi-teacher rows — shown for sections with a teachers[] array.
              Each teacher signs independently. Section is "signed" only when
              all teachers have signed. */}
          {hasTeachers && (
            <>
              {teachersHeader}
              <div style={styles.teacherList}>
                {data.teachers.map((t, idx) => {
                  const isMine =
                    myStaffSisId &&
                    t.sisId &&
                    String(t.sisId) === myStaffSisId;
                  const slotSigned = !!t.signedAt;
                  const slotKey = `${section.id}-${t.sisId || idx}`;
                  return (
                    <div key={slotKey} style={styles.teacherRow}>
                      <div style={styles.teacherRowMain}>
                        <span style={styles.teacherRowName}>
                          {t.name || '(unnamed)'}
                          {t.sisId && (
                            <span style={styles.teacherRowSisId}>
                              {' '}
                              · {t.sisId}
                            </span>
                          )}
                          {isMine && (
                            <span style={styles.teacherRowMineTag}>you</span>
                          )}
                        </span>
                        {slotSigned ? (
                          <span style={styles.teacherRowSigned}>
                            ✓ Signed by{' '}
                            <strong>{t.signedByName || 'Unknown'}</strong>
                            {t.signedAt && (
                              <>
                                {' '}
                                &nbsp;•&nbsp;{' '}
                                {formatSignoffTimestamp(t.signedAt)}
                              </>
                            )}
                          </span>
                        ) : (
                          <span style={styles.teacherRowPending}>
                            Pending
                          </span>
                        )}
                      </div>
                      <div style={styles.teacherRowActions}>
                        {/* Show Sign Off if: my slot OR I'm a section signer
                            (canSign already verifies that) OR superadmin.
                            Department admins on the section's signers list
                            can cover any teacher's slot — needed for
                            end-of-year sweeps when the actual teacher is
                            unavailable. */}
                        {!slotSigned && canSign && (
                          <button
                            type="button"
                            style={styles.actionBtnSmall('primary')}
                            onClick={() =>
                              onSign(section.id, { teacherSisId: t.sisId })
                            }
                          >
                            Sign off
                          </button>
                        )}
                        {/* Already signed → my slot, section signer, or
                            admin can undo. Same broadened access as Sign
                            Off for symmetry. */}
                        {slotSigned && canSign && (
                          <button
                            type="button"
                            style={styles.actionBtnSmall('secondary')}
                            onClick={() =>
                              onUndo(section.id, { teacherSisId: t.sisId })
                            }
                          >
                            Undo
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Section-level signoff metadata: only shown for legacy
              single-signer sections. For multi-teacher, the per-teacher
              rows above show this info. */}
          {!hasTeachers && status === SECTION_STATUS.SIGNED && (
            <div style={styles.signoffInfo}>
              Signed off by{' '}
              <strong style={{ color: KA_COLORS.navy }}>
                {data.signedByName || 'Unknown'}
              </strong>
              {data.signedAt && (
                <> &nbsp;•&nbsp; {formatSignoffTimestamp(data.signedAt)}</>
              )}
              {data.notes && data.notes.trim().length > 0 && (
                <div style={styles.notesBox}>
                  <strong>Notes:</strong> {data.notes}
                </div>
              )}
            </div>
          )}

          {/* Dorm inventory preview when signed */}
          {section.id === 'dorm' &&
            status === SECTION_STATUS.SIGNED &&
            data.inventory && (
              <div style={styles.inventoryPreview}>
                <div style={styles.inventoryHeading}>
                  Inventory (
                  {Object.values(data.inventory).filter(Boolean).length} of{' '}
                  {Object.keys(data.inventory).length} items)
                </div>
                {data.damageNotes && data.damageNotes.trim().length > 0 && (
                  <div style={{ marginTop: 4, color: '#C62828' }}>
                    <strong>Damage:</strong> {data.damageNotes}
                  </div>
                )}
              </div>
            )}

          {Array.isArray(data?.signoffHistory) && data.signoffHistory.length > 0 && (
            <SignoffHistoryView history={data.signoffHistory} />
          )}
        </div>

        <div style={styles.rightCol}>
          <span style={styles.statusPill(status)}>
            {getStatusLabel(status)}
          </span>
          {actions}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------

const CheckoutStudentDetail = ({ recordId, user, onBack }) => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSectionEditor, setShowSectionEditor] = useState(false);


  // Modal state
  const [signoffModal, setSignoffModal] = useState(null); // { sectionId, mode }
  const [showFormChange, setShowFormChange] = useState(false);

  useEffect(() => {
    if (!recordId) return undefined;
    const ref = doc(db, 'checkoutRecords', recordId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setRecord(null);
          setError('Checkout record not found.');
          setLoading(false);
          return;
        }
        setRecord({ id: snap.id, ...snap.data() });
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error('Failed to load checkout record:', err);
        setError(err.message || 'Failed to load record.');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [recordId]);

  const completion = useMemo(() => computeCompletionPercent(record), [record]);
  const relevantSectionIds = useMemo(
    () => (record ? getRelevantSectionIds(record) : []),
    [record],
  );
  const sectionConfigs = useMemo(
    () =>
      relevantSectionIds
        .map((id) => CHECKOUT_SECTIONS.find((s) => s.id === id))
        .filter(Boolean),
    [relevantSectionIds],
  );
  const groupedSections = useMemo(
    () => groupSections(sectionConfigs),
    [sectionConfigs],
  );

  const formConfig = record ? getFormConfig(record.formType) : null;

  // Optional `opts` carries multi-teacher context: { teacherSisId } when
  // the user clicks Sign/Undo on a specific teacher row inside a section
  // that has a teachers[] array. The modal forwards this on to the Cloud
  // Function so the right teacher slot is updated.
  const openSignoff = (sectionId, opts = {}) =>
    setSignoffModal({ sectionId, mode: 'sign', teacherSisId: opts.teacherSisId || null });
  const openUndo = (sectionId, opts = {}) =>
    setSignoffModal({ sectionId, mode: 'undo', teacherSisId: opts.teacherSisId || null });
  const openMarkNa = (sectionId) =>
    setSignoffModal({ sectionId, mode: 'na', teacherSisId: null });

  const closeSignoffModal = () => setSignoffModal(null);

  const handleDownloadPdf = () => {
    if (!record) return;
    try {
      generateCheckoutPdf(record);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('PDF generation failed. See console for details.');
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingBox}>Loading checkout record…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.topBar}>
          <button type="button" style={styles.backBtn} onClick={onBack}>
            ← Back
          </button>
        </div>
        <div style={styles.errorBox}>{error}</div>
      </div>
    );
  }

  if (!record) return null;

  // Decide whether to render the dorm modal vs generic modal
  const renderSignoffModal = () => {
    if (!signoffModal) return null;
    const { sectionId, mode, teacherSisId } = signoffModal;
    const section = getSectionConfig(sectionId);
    if (!section) return null;

    if (sectionId === 'dorm' && (mode === 'sign' || mode === 'undo')) {
      return (
        <CheckoutDormSignoffModal
          record={record}
          mode={mode}
          onClose={closeSignoffModal}
          onSuccess={closeSignoffModal}
        />
      );
    }
    return (
      <CheckoutSignoffModal
        record={record}
        sectionId={sectionId}
        teacherSisId={teacherSisId}
        user={user}
        mode={mode}
        onClose={closeSignoffModal}
        onSuccess={closeSignoffModal}
      />
    );
  };

  const isArchived = record.status === RECORD_STATUS.ARCHIVED;

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button type="button" style={styles.backBtn} onClick={onBack}>
          ← Back to dashboard
        </button>
        <div style={styles.spacer} />
        <button type="button" style={styles.iconBtn} onClick={handleDownloadPdf}>
          Download PDF
        </button>
    {user?.isSuperAdmin && !isArchived && (
            <button
                type="button"
                style={styles.iconBtn}
                onClick={() => setShowFormChange(true)}
            >
                Change form type
            </button>
            )}
            {user?.isSuperAdmin && !isArchived && (
            <button
                type="button"
                style={styles.iconBtn}
                onClick={() => setShowSectionEditor(true)}
                title="Toggle sections between Pending and N/A for this record"
            >
                Edit Sections
            </button>
            )}
        </div>

      {isArchived && (
        <div
          style={{
            ...styles.errorBox,
            background: '#E3F2FD',
            border: '1px solid #BBDEFB',
            color: '#1565C0',
          }}
        >
          This record is from school year <strong>{record.schoolYear}</strong>{' '}
          and is archived. Read-only.
        </div>
      )}

      <div style={styles.headerCard}>
        <h1 style={styles.studentName}>{record.studentName || 'Unknown'}</h1>
        <p style={styles.studentMeta}>
          {[record.grade, record.cohort, record.studentId && `SIS ${record.studentId}`,
            record.dorm && `Dorm: ${record.dorm}`,
            record.room && `Room: ${record.room}`]
            .filter(Boolean)
            .join('  •  ')}
        </p>
        <div style={styles.metaRow}>
          {formConfig && <span style={styles.formPill}>{formConfig.label}</span>}
          <span style={{ fontSize: 13, color: '#666' }}>
            School year: <strong>{record.schoolYear}</strong>
          </span>
        </div>
        <div style={{ ...styles.metaRow, marginTop: 12 }}>
          <div style={styles.progressOuter}>
            <div
              style={styles.progressInner(completion, completion === 100)}
            />
          </div>
          <div style={styles.progressLabel}>{completion}% complete</div>
        </div>
        {record.formAssignedByName && (
          <p
            style={{
              fontSize: 11,
              color: '#999',
              margin: '4px 0 0',
              fontStyle: 'italic',
            }}
          >
            Form assigned by {record.formAssignedByName}
            {record.formAssignedAt && (
              <> on {formatSignoffTimestamp(record.formAssignedAt)}</>
            )}
          </p>
        )}
      </div>

      {/* Sections, grouped */}
      {groupedSections.map((group) => (
        <div key={group.groupName || '__top__'}>
          {group.groupName && (
            <h2 style={styles.groupHeader}>{group.groupName}</h2>
          )}
          <div style={styles.sectionsList}>
            {group.sections.map((section) => (
              <SectionCard
                key={section.id}
                section={section}
                data={record.sections?.[section.id]}
                user={user}
                onSign={isArchived ? () => {} : openSignoff}
                onUndo={isArchived ? () => {} : openUndo}
                onMarkNa={isArchived ? () => {} : openMarkNa}
              />
            ))}
          </div>
        </div>
      ))}

    {!isArchived && renderSignoffModal()}
        {showFormChange && !isArchived && (
            <CheckoutFormChangeModal
            record={record}
            onClose={() => setShowFormChange(false)}
            onChanged={() => setShowFormChange(false)}
            />
        )}
        {showSectionEditor && !isArchived && (
            <EditRecordSectionsModal
            record={record}
            onClose={() => setShowSectionEditor(false)}
            onSaved={() => setShowSectionEditor(false)}
            />
        )}
        </div>
    );
};

export default CheckoutStudentDetail;